import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons'

export const MAP_LOADING_FLAVOR = [
  'Warming up the Control Center…',
  'Loading jobs onto the map…',
  'Finding people in the field…',
  'Checking fleet assets…',
  'Pulling municipality data…',
  'Almost ready to search…',
]

const SOURCES = [
  { key: 'jobs', label: 'Jobs' },
  { key: 'people', label: 'People' },
  { key: 'assets', label: 'Fleet' },
  { key: 'municipalities', label: 'Municipalities' },
] as const

export function MapLoadingOverlay({
  jobsLoading,
  peopleLoading,
  assetsLoading,
  municipalitiesLoading,
  offsetForToolbar = false,
}: {
  jobsLoading: boolean
  peopleLoading: boolean
  assetsLoading: boolean
  municipalitiesLoading: boolean
  offsetForToolbar?: boolean
}) {
  const [flavorIdx, setFlavorIdx] = useState(0)
  const loadingByKey = {
    jobs: jobsLoading,
    people: peopleLoading,
    assets: assetsLoading,
    municipalities: municipalitiesLoading,
  }

  useEffect(() => {
    const id = window.setInterval(() => {
      setFlavorIdx((i) => (i + 1) % MAP_LOADING_FLAVOR.length)
    }, 2200)
    return () => window.clearInterval(id)
  }, [])

  return (
    <div
      className={`absolute inset-0 z-[15] flex items-center justify-center bg-base-300/65 backdrop-blur-[2px] ${
        offsetForToolbar ? 'pt-14' : ''
      }`}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading Control Center map data"
    >
      <div className="mx-4 flex w-full max-w-xs flex-col items-center gap-4 rounded-2xl border-2 border-base-300 bg-base-100 px-6 py-6 shadow-xl">
        <span className="loading loading-spinner loading-lg text-primary" />
        <div className="text-center">
          <p className="text-base font-semibold">Loading Control Center</p>
          <p className="mt-1 min-h-5 text-sm text-base-content/70">
            {MAP_LOADING_FLAVOR[flavorIdx]}
          </p>
        </div>
        <ul className="w-full space-y-1.5">
          {SOURCES.map((source) => {
            const loading = loadingByKey[source.key]
            return (
              <li key={source.key} className="flex items-center justify-between gap-3 text-sm">
                <span className={loading ? 'text-base-content/65' : 'text-base-content'}>
                  {source.label}
                </span>
                {loading ? (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="animate-spin text-xs text-base-content/45"
                    aria-label={`${source.label} still loading`}
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="text-xs text-success"
                    aria-label={`${source.label} loaded`}
                  />
                )}
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
